/**
 * US federal tax constants and maths, tax year 2026 (IRS Rev. Proc. 2025-32; SSA 2026 wage base). One shared table for the
 * Income Tax, Self-Employment Tax and State Tax calculators. Review each October/November when the IRS and SSA publish
 * the next year's figures. Head of household and married-filing-separately values should be re-checked against the
 * Revenue Procedure when this file is next updated.
 */
export const US_TAX_YEAR = 2026;
export type Filing = "single" | "mfj" | "mfs" | "hoh";
export const FILING_LABEL: Record<Filing, string> = { single: "Single", mfj: "Married filing jointly", mfs: "Married filing separately", hoh: "Head of household" };

export interface Bracket { upTo: number; rate: number }
const B = (caps: number[]): Bracket[] => [0.1, 0.12, 0.22, 0.24, 0.32, 0.35, 0.37].map((rate, i) => ({ upTo: caps[i] ?? Infinity, rate }));

export const BRACKETS: Record<Filing, Bracket[]> = {
  single: B([12400, 50400, 105700, 201775, 256225, 640600]),
  mfj: B([24800, 100800, 211400, 403550, 512450, 768700]),
  mfs: B([12400, 50400, 105700, 201775, 256225, 384350]),
  hoh: B([17700, 67450, 105700, 201750, 256200, 640600]),
};
export const STANDARD_DEDUCTION: Record<Filing, number> = { single: 16100, mfj: 32200, mfs: 16100, hoh: 24150 };

export const SS_WAGE_BASE = 184500;
export const SS_RATE = 0.124; // combined; employees pay half
export const MEDICARE_RATE = 0.029;
export const ADDL_MEDICARE_RATE = 0.009;
export const ADDL_MEDICARE_THRESHOLD: Record<Filing, number> = { single: 200000, mfj: 250000, mfs: 125000, hoh: 200000 };
export const SE_MIN_EARNINGS = 400;
export const SE_NET_FACTOR = 0.9235;

export function bracketTax(taxable: number, brackets: Bracket[]): number {
  let tax = 0, last = 0;
  for (const b of brackets) {
    if (taxable <= last) break;
    tax += (Math.min(taxable, b.upTo) - last) * b.rate;
    last = b.upTo;
  }
  return tax;
}

export function marginalRate(taxable: number, brackets: Bracket[]): number {
  for (const b of brackets) if (taxable <= b.upTo) return b.rate;
  return brackets[brackets.length - 1].rate;
}

export interface SeTax { netEarnings: number; socialSecurity: number; medicare: number; additionalMedicare: number; total: number; deductibleHalf: number; belowMinimum: boolean }

/**
 * Self-employment tax. `w2Wages` use up part of the Social Security wage base and count towards the Additional Medicare
 * threshold. The Additional Medicare Tax (0.9%) is shown separately and is not part of the deductible half.
 */
export function selfEmploymentTax(profit: number, filing: Filing = "single", w2Wages = 0): SeTax {
  const net = Math.max(0, profit) * SE_NET_FACTOR;
  if (net < SE_MIN_EARNINGS) return { netEarnings: net, socialSecurity: 0, medicare: 0, additionalMedicare: 0, total: 0, deductibleHalf: 0, belowMinimum: profit > 0 };
  const room = Math.max(0, SS_WAGE_BASE - Math.max(0, w2Wages));
  const ss = Math.min(net, room) * SS_RATE;
  const med = net * MEDICARE_RATE;
  const addl = Math.max(0, net - Math.max(0, ADDL_MEDICARE_THRESHOLD[filing] - Math.max(0, w2Wages))) * ADDL_MEDICARE_RATE;
  return { netEarnings: net, socialSecurity: ss, medicare: med, additionalMedicare: addl, total: ss + med + addl, deductibleHalf: (ss + med) / 2, belowMinimum: false };
}

export interface Fica { socialSecurity: number; medicare: number; additionalMedicare: number; total: number }
export function employeeFica(wages: number, filing: Filing = "single"): Fica {
  const w = Math.max(0, wages);
  const ss = Math.min(w, SS_WAGE_BASE) * (SS_RATE / 2);
  const med = w * (MEDICARE_RATE / 2);
  const addl = Math.max(0, w - ADDL_MEDICARE_THRESHOLD[filing]) * ADDL_MEDICARE_RATE;
  return { socialSecurity: ss, medicare: med, additionalMedicare: addl, total: ss + med + addl };
}

export interface Qbi { amount: number; qualifiedIncome: number; applied: boolean; overThreshold: boolean }
/**
 * Qualified business income deduction (IRC section 199A), simple case: 20% of qualified business income, limited to 20% of
 * taxable income before this deduction. QBI is the profit less the deductible half of self-employment tax. Above the
 * taxable-income threshold (the top of the 24% bracket) the wage/property limits and the specified-service-trade phase-out
 * apply; they need inputs this calculator does not take, so no deduction is taken there (overThreshold is true).
 */
export function qbiDeduction(profit: number, deductibleHalf: number, taxableBeforeQbi: number, filing: Filing): Qbi {
  const qualifiedIncome = Math.max(0, profit - deductibleHalf);
  const threshold = BRACKETS[filing][3].upTo;
  const taxable = Math.max(0, taxableBeforeQbi);
  if (qualifiedIncome <= 0 || taxable <= 0) return { amount: 0, qualifiedIncome, applied: false, overThreshold: false };
  if (taxable > threshold) return { amount: 0, qualifiedIncome, applied: false, overThreshold: true };
  return { amount: Math.min(qualifiedIncome * 0.2, taxable * 0.2), qualifiedIncome, applied: true, overThreshold: false };
}
